"use client";

import type { ReactNode } from "react";
import { CheckCircle2 } from "lucide-react";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

interface ServiceUpgradeDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description: ReactNode;
  children: ReactNode;
}

interface ServiceUpgradeIntroProps {
  label: string;
  priceLabel: string;
  priceAnchorLabel?: string;
  discountLabel?: string;
  priceCaption: string;
  audienceNote: string;
  benefits: string[];
  error?: string | null;
  secondaryAction: ReactNode;
  primaryAction: ReactNode;
}

export function ServiceUpgradeDialog({
  open,
  onOpenChange,
  title,
  description,
  children,
}: ServiceUpgradeDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[90vh] flex-col rounded-2xl border border-border bg-background p-0 shadow-xl sm:max-w-xl">
        <div className="border-b border-border bg-muted/40 px-5 py-5 sm:px-6">
          <DialogHeader className="pr-10">
            <DialogTitle className="text-xl tracking-tight text-foreground sm:text-2xl">
              {title}
            </DialogTitle>
            <DialogDescription className="text-sm leading-6 text-muted-foreground">
              {description}
            </DialogDescription>
          </DialogHeader>
        </div>

        <div className="min-h-0 flex-1 space-y-5 overflow-y-auto px-5 pb-5 pt-5 sm:px-6">
          {children}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function ServiceUpgradeIntro({
  label,
  priceLabel,
  priceAnchorLabel,
  discountLabel,
  priceCaption,
  audienceNote,
  benefits,
  error,
  secondaryAction,
  primaryAction,
}: ServiceUpgradeIntroProps) {
  return (
    <>
      <div className="rounded-2xl border border-primary/30 bg-primary/5 p-4">
        <p className="text-xs font-semibold uppercase tracking-wide text-primary">
          {label}
        </p>
        <div className="mt-1 flex flex-col gap-1 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <p className="text-3xl font-semibold tracking-tight text-foreground">
              {priceLabel}
            </p>
            {priceAnchorLabel || discountLabel ? (
              <div className="mt-1 flex items-center text-xs text-green-600">
                {priceAnchorLabel ? (
                  <span className="line-through text-muted-foreground">
                    {priceAnchorLabel}
                  </span>
                ) : null}
                {discountLabel ? (
                  <span className="ml-2 font-semibold">{discountLabel}</span>
                ) : null}
              </div>
            ) : null}
          </div>
          <p className="text-sm text-muted-foreground">{priceCaption}</p>
        </div>
        <p className="mt-3 text-sm leading-6 text-muted-foreground">
          {audienceNote}
        </p>
      </div>

      <div className="grid gap-3">
        {benefits.map((benefit) => (
          <div
            key={benefit}
            className="flex gap-3 rounded-xl border border-border bg-card p-3 text-sm text-muted-foreground"
          >
            <CheckCircle2
              className="mt-0.5 h-4 w-4 shrink-0 text-primary"
              aria-hidden="true"
            />
            <span>{benefit}</span>
          </div>
        ))}
      </div>

      {error ? (
        <p className="rounded-lg border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">
          {error}
        </p>
      ) : null}

      <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
        {secondaryAction}
        {primaryAction}
      </div>
    </>
  );
}
